/**
 * URL normalisation for survey outcome callbacks. Runs once, synchronously,
 * from index.tsx before the router boots.
 *
 * External survey providers (CloudResearch/Sentry, Confirmit) redirect
 * respondents back to our callback URLs and append their own tracking params.
 * Standard URL semantics put an appended query string BEFORE the fragment, so
 * the callback URL arrives looking like this:
 *
 *   /?providerId=X&userid=Y#/surveys/qualityterminate?surveyid=810931
 *    └────── theirs ──────┘ └──────────── ours ─────────────────────┘
 *
 * Our callback routes are hash routes, so everything the app reads lives after
 * the '#'. The pre-hash half is invisible to HashRouter and to any code reading
 * the hash-internal query string, so `userid` above is simply lost, the callback
 * page renders "Invalid survey link", and the outcome is never recorded.
 *
 * We cannot control how providers build these URLs, and the fix must not depend
 * on every callback URL having been configured correctly, so it lives entirely
 * on our side and runs before anything reads the URL.
 *
 * Two separate things happen here, in this order:
 *
 *   1. mergePreHashParamsIntoHashRoute — folds the pre-hash query string into
 *      the hash-internal one, leaving nothing before the '#'.
 *   2. rescueHashlessCallbackPath — handles the other way a callback URL gets
 *      mangled: the '#' is dropped entirely and the outcome route arrives as a
 *      real path. Strictly limited to the four callback routes.
 *
 * Nothing here ever throws. A tracking-param fixup must never be the reason the
 * app fails to boot.
 */

/**
 * The only paths rescueHashlessCallbackPath will act on.
 *
 * Deliberately an exhaustive literal list rather than a pattern: a wrong guess
 * here sends a respondent to an arbitrary route instead of failing visibly, so
 * the set of URLs whose behaviour changes must be enumerable by reading this
 * file. Keep in sync with the callback routes in App.tsx.
 */
const RESCUABLE_CALLBACK_PATHS = [
    '/surveys/completed',
    '/surveys/terminate',
    '/surveys/overquota',
    '/surveys/qualityterminate',
];

/** Splits "#/surveys/completed?a=1" into { path: '/surveys/completed', query: 'a=1' }. */
function splitHash(hash: string): { path: string; query: string } {
    const raw = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    const queryStart = raw.indexOf('?');
    return queryStart === -1
        ? { path: raw, query: '' }
        : { path: raw.slice(0, queryStart), query: raw.slice(queryStart + 1) };
}

/** Normalises a pathname for comparison against RESCUABLE_CALLBACK_PATHS. */
function canonicalPath(pathname: string): string {
    return (pathname || '').replace(/\/+$/, '').toLowerCase();
}

/**
 * STEP 1 — merge the pre-hash query string into the hash-internal one.
 *
 * Returns true when the URL was rewritten, false when this step declined to act
 * and the caller should fall through to step 2.
 */
function mergePreHashParamsIntoHashRoute(search: string, hash: string): boolean {
    const { path, query } = splitHash(hash);

    // A bare '#' with no route behind it gives us nowhere to merge into, and
    // inventing a route would change where the respondent lands. Decline.
    if (!path) {
        return false;
    }

    const merged = new URLSearchParams(query);
    const incoming = new URLSearchParams(search);
    const handled: { [key: string]: true } = {};

    incoming.forEach((_value, key) => {
        // getAll() below consumes every value for a key on first sight, so a
        // repeated key must not be processed again.
        if (handled[key]) {
            return;
        }
        handled[key] = true;

        // Hash-internal params win. Ours were put there by the callback URL we
        // published; theirs are appended noise that may collide by name. Never
        // overwrite a key that is already present.
        if (merged.has(key)) {
            return;
        }

        incoming.getAll(key).forEach((value) => merged.append(key, value));
    });

    const mergedQuery = merged.toString();

    // pathname is preserved; only the query string moves. Nothing is left
    // before the '#'.
    replaceUrl(
        window.location.pathname + '#' + path + (mergedQuery ? '?' + mergedQuery : ''),
    );
    return true;
}

/**
 * STEP 2 — rescue a callback URL whose '#' was dropped entirely, e.g.
 *
 *   /surveys/qualityterminate?surveyid=810931&userid=Y
 *
 * nginx serves index.html for that path, HashRouter finds no hash and settles
 * on '/', and the respondent lands on the admin login screen with the outcome
 * unrecorded. Rewriting to the equivalent hash route records it normally.
 *
 * Anything not on RESCUABLE_CALLBACK_PATHS is left completely untouched.
 *
 * Returns true when the URL was rewritten.
 */
function rescueHashlessCallbackPath(search: string): boolean {
    const pathname = canonicalPath(window.location.pathname);

    if (RESCUABLE_CALLBACK_PATHS.indexOf(pathname) === -1) {
        return false;
    }

    const query = search.charAt(0) === '?' ? search.slice(1) : search;
    replaceUrl('/#' + pathname + (query ? '?' + query : ''));
    return true;
}

function replaceUrl(url: string): void {
    window.history.replaceState(null, '', url);
}

/**
 * Fold any params sitting before the '#' into the hash route, so that every
 * downstream reader of the hash-internal query string sees the full set.
 *
 * Safe to call unconditionally on any route: with no pre-hash query string it
 * returns immediately without touching the URL.
 */
export function normalizeTrackingParams(): void {
    try {
        if (typeof window === 'undefined' || !window.location || !window.history) {
            return;
        }

        // Nothing before the '#' means there is nothing to move. This is the
        // common case — every correctly formed URL takes it — so it stays a
        // cheap no-op.
        const search = window.location.search;
        if (!search || search === '?') {
            return;
        }

        const hash = window.location.hash || '';
        if (hash && mergePreHashParamsIntoHashRoute(search, hash)) {
            return;
        }

        rescueHashlessCallbackPath(search);
    } catch (err) {
        // Deliberately swallowed. A malformed URL, a locked-down history API or
        // a cross-origin replaceState must degrade to "params not normalised",
        // never to a blank app.
    }
}
